import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import Button from "../../componentes/button/button";
import TabelaRetornosDeEntregas from "./escala-de-rotas/TabelaRetornosDeEntregas";
import { CustomTable } from "../../componentes/table/CustomTable";
import { retornaTipoDaRota } from "../../utils/options";
import CargaEntregaService from "../../services/resource/escalaDeRotaService";
import { capitalizeNome } from "../../utils/reduzirNome";
import { formatLocalDate, formatLocalDateTime } from "../../utils/formatDate";
import ResumoRota from "./ResumoRota";
import SolicitarAjusteRapido from "./ajustes/SolicitarAjusteRapido";
import { toast } from "react-toastify";
import { useAuth } from "../../context/authContext";
import { Loading } from "../../componentes/loading/loading";
import DateMasked from "../../componentes/inputMask/DateMasked";

function TabelaCargaEntrega({ ano, mes, funcionarioId, filialId }) {
    const { user } = useAuth();

    const roleAdmin = user.perfis.includes('ADMIN');

    const cargaService = new CargaEntregaService();
    const [modalDeRetornos, setModalRetornos] = useState(false);
    const [modalResumo, setModalResumo] = useState(false);
    const [cargaParaAjuste, setCargaParaAjuste] = useState(null);
    const [modalAlterarData, setModalAlterarData] = useState(false);
    const [novaDataOperacao, setNovaDataOperacao] = useState("");
    const [salvandoData, setSalvandoData] = useState(false);
    const [selectedCarga, setSelectedCarga] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    // só a resposta da última consulta vale: trocar mês/filial rápido não pode mostrar dado de período antigo
    const ultimaConsulta = useRef(0);

    const dadosFiltrados = useMemo(() => data ?? [], [data]);

    useEffect(() => {
        if (ano && mes) {
            buscarCargas();
        }
    }, [ano, mes, funcionarioId, filialId]);

    const buscarCargas = () => {
        const consulta = ++ultimaConsulta.current;
        setLoading(true);
        cargaService.listarPorPeriodo(funcionarioId, ano, mes, filialId)
            .then(res => {
                if (consulta === ultimaConsulta.current) setData(res.data);
            })
            .catch(e => console.error(e))
            .finally(() => {
                if (consulta === ultimaConsulta.current) setLoading(false);
            });
    };

    const excluir = () => {
        if (window.confirm("Você confirma o cancelamento desta carga? ") === true) {
            cargaService.excluirCarga(selectedCarga.id).then(() => {

                setData(prev =>
                    prev.filter(carga => carga.id !== selectedCarga.id)
                );

                toast.success("Carga deletada com sucesso");
                setModalResumo(false);
                setSelectedCarga(null);
            });
        }
    }

    const abrirAlterarData = () => {
        setNovaDataOperacao(selectedCarga?.dataOperacao ?? "");
        setModalAlterarData(true);
    };

    // Só o dia de operação muda; a regra de comissão fechada é validada no backend
    const confirmarAlteracaoData = async () => {
        if (!novaDataOperacao) {
            toast.error("Informe a nova data de operação.");
            return;
        }
        if (novaDataOperacao === selectedCarga.dataOperacao) {
            setModalAlterarData(false);
            return;
        }

        setSalvandoData(true);
        try {
            await cargaService.atualizarDataOperacao(selectedCarga.id, novaDataOperacao);

            setData(prev => (prev ?? []).map(c =>
                String(c.id) === String(selectedCarga.id) ? { ...c, dataOperacao: novaDataOperacao } : c));
            setSelectedCarga(prev => ({ ...prev, dataOperacao: novaDataOperacao }));

            toast.success("Data de operação alterada. Gere a comissão novamente para as competências afetadas.");
            setModalAlterarData(false);
        } catch (e) {
            console.error(e);
        } finally {
            setSalvandoData(false);
        }
    };

    const formatPercent = (v) =>
        v != null ? `${Number(v).toFixed(2)}%` : "—";

    const formatNumber = (v) =>
        v != null ? Number(v).toLocaleString("pt-BR") : "—";

    const abrirRetornos = (row) => {
        setSelectedCarga({
            filialId: row.filialId,
            transporte: row.transporte
        });
        setModalRetornos(true);
    };

    const onRowClick = (item) => {
        setSelectedCarga(item);
        setModalResumo(true);
    }

    const columns = useMemo(() => [

        { key: "filialNome", label: "Filial", sortable: true, width: 140 },

        {
            key: "dataOperacao",
            label: "Data",
            sortable: true,
            render: v => formatLocalDate(v)
        },

        { key: "carga", label: "Carga", sortable: true },

        { key: "transporte", label: "Transporte", sortable: true },

        {
            key: "motoristaNome",
            label: "Motorista",
            render: (item) => capitalizeNome(item)
        },

        {
            key: "auxiliar1Nome",
            label: "Auxiliar 1",
            render: (item) => capitalizeNome(item)
        },
        {
            key: "auxiliar2Nome",
            label: "Auxiliar 2",
            render: (item) => capitalizeNome(item)
        },

        { key: "tipoRota", label: "Tipo Rota", sortable: true, render: (item) => retornaTipoDaRota(item) },

        { key: "veiculoPlaca", label: "Veículo", sortable: true },

        {
            key: "cubos",
            label: "Cubos",
            center: true,
            sortable: true,
            render: v => formatNumber(v)
        },

        {
            key: "ocupacao",
            label: "% Ocupação",
            center: true,
            sortable: true,
            render: v => formatPercent(v)
        },

        {
            key: "rotaGreenmileSaidaRealizada",
            label: "Saída Real",
            sortable: true,
            className: "bg-green-50",
            render: v => formatLocalDateTime(v)
        },

        {
            key: "rotaGreenmileChegadaRealizada",
            label: "Chegada Real",
            sortable: true,
            className: "bg-green-50",
            render: v => formatLocalDateTime(v)
        },

        {
            key: "rotaGreenmileDescricaoCarga",
            label: "Carga GM",
            sortable: true,
            className: "bg-green-50"
        },

        {
            key: "rotaGreenmileEntregueNoPrazo",
            label: "Status GM",
            sortable: true,
            className: "bg-green-50",
            render: v => {
                if (v === "No prazo") return <Badge label="No Prazo" className="text-green-700 bg-green-100" />;
                if (v === "Fora do prazo") return <Badge label="Fora do Prazo" className="text-red-700 bg-red-100" />;
                return <Badge label="Indeterminado" className="text-gray-700 bg-gray-200" />;
            }
        },

        {
            key: "rotaGreenmileImagens",
            label: "Imagens",
            sortable: true,
            className: "bg-green-50",
            render: v => v === true || v === "true"
                ? <Badge label="true" className="text-green-700 bg-green-100" />
                : <Badge label="false" className="text-red-700 bg-red-100" />
        },

        {
            key: "entregaVolumeTaxaOcupacao",
            label: "Tx ocupação",
            sortable: true,
            className: "bg-red-50"
        },

        {
            key: "entregaVolumeTotalCliente",
            label: "Total Clientes",
            sortable: true,
            className: "bg-red-50",
            render: v => formatNumber(v)
        },

        {
            key: "entregaVolumeCubos",
            label: "Cubos",
            sortable: true,
            className: "bg-red-50",
            render: v => formatNumber(v)
        },

        {
            key: "entregaVolumeCaixasUnitarias",
            label: "Caixas Uni.",
            sortable: true,
            className: "bg-red-50",
            render: v => formatNumber(v)
        },

        {
            key: "entregaVolumeDataEntrega",
            label: "Data Entrega",
            sortable: true,
            className: "bg-red-50",
            render: v => formatLocalDate(v)
        },

        {
            key: "entregaVolumeDataRetorno",
            label: "Data Retorno",
            sortable: true,
            className: "bg-red-50",
            render: v => formatLocalDate(v)
        },

        {
            key: "totalCubosRetornados",
            label: "Cubos Retornados",
            render: (_, row) =>
                row.temRetornos
                    ? (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                abrirRetornos(row);
                            }}
                            className="underline text-primary cursor-pointer hover:brightness-90 transition-colors duration-150"
                        >
                            {row.totalCubosRetornados}
                        </span>
                    )
                    : "—"
        },
        
        {
            key: "ajuste",
            label: "Ajuste",
            center: true,
            noSelected: true,
            render: (_, row) => (
                <button
                    type="button"
                    title="Solicitar ajuste de comissão desta escala"
                    onClick={(e) => {
                        e.stopPropagation();
                        setCargaParaAjuste(row);
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[10px] font-semibold text-foreground hover:bg-muted transition-colors duration-150"
                >
                    <i className="fa fa-wrench text-teal-600" /> Ajustar
                </button>
            )
        },

    ], []);

    const footer = () => {
        const totais = dadosFiltrados.reduce(
            (acc, row) => ({
                caixasEntregue: acc.caixasEntregue + (Number(row.caixasEntregue) || 0),
                caixasDevolvida: acc.caixasDevolvida + (Number(row.caixasDevolvida) || 0),
                cubosEntregue: acc.cubosEntregue + (Number(row.entregaVolumeCubos) || 0),
                cubosDevolvido: acc.cubosDevolvido + (Number(row.totalCubosRetornados) || 0),
                quantidadeEntregas: acc.quantidadeEntregas + (Number(row.entregaVolumeTotalCliente) || 0),
                quantidadePedidos: acc.quantidadePedidos + (Number(row.quantidadePedidos) || 0),
            }),
            {
                caixasEntregue: 0,
                caixasDevolvida: 0,
                cubosEntregue: 0,
                cubosDevolvido: 0,
                quantidadeEntregas: 0,
                quantidadePedidos: 0,
            }
        );

        return (
            <div className="flex flex-wrap justify-between gap-2 p-2 text-[11px] border-t border-border bg-muted">
                <div className="font-semibold">
                    Total de Cargas: {dadosFiltrados.length}
                </div>
                <div className="flex flex-wrap gap-4">
                    <span><span className="font-semibold">QTD Ent.:</span> {formatNumber(totais.quantidadeEntregas)}</span>
                    <span><span className="font-semibold">Cubagem:</span> {formatNumber(totais.cubosEntregue)}</span>
                    <span><span className="font-semibold">Cubos Ret.:</span> {formatNumber(totais.cubosDevolvido)}</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            {!funcionarioId ?
                (loading && data === null) ? (
                    <Loading label="Carregando escalas de rota do período..." />
                ) : (
                    <div className="relative">
                        {loading && (
                            <div className="absolute inset-0 z-10 flex items-start justify-center bg-background/60 pt-24">
                                <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-xs font-medium shadow-card">
                                    <i className="fas fa-spinner fa-spin text-primary" /> Atualizando escalas...
                                </div>
                            </div>
                        )}
                        <CustomTable
                            data={dadosFiltrados}
                            columns={columns}
                            loading={loading}
                            hideLoadingSkeleton
                            maxHeight="calc(100vh - 285px)"
                            className="text-[11px]"
                            showHeader={false}
                            pagination
                            pageSize={20}
                            onRowSelect={(row) => onRowClick(row)}
                        />
                    </div>
                )
                :
                <CustomTable
                    data={dadosFiltrados}
                    columns={columns}
                    loading={loading}
                    hideLoadingSkeleton
                    maxHeight="420px"
                    className="text-[11px]"
                    showHeader={false}
                    footer={footer}
                    exportFilename={"Cargas " + funcionarioId}
                    exportable
                />
            }

            <Dialog
                visible={modalDeRetornos}
                onHide={() => setModalRetornos(false)}
                className="w-[95%]"
                blockScroll
                contentClassName="p-0"
                footer={
                    <div className="flex justify-end pt-3">
                        <Button
                            variant="gray"
                            label="Voltar"
                            iconLeft="fa fa-arrow-left"
                            onClick={() => setModalRetornos(false)}
                        />
                    </div>
                }
            >
                <TabelaRetornosDeEntregas values={selectedCarga} />
            </Dialog>

            <Dialog
                visible={modalResumo}
                onHide={() => { setModalResumo(false); setSelectedCarga(null) }}
                className="w-[95%]"
                blockScroll
                contentClassName="p-0"
                headerClassName="p-4"
                header={
                    <div className="px-2  flex items-center justify-between gap-4">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-lg">🚚</span>
                                <span className="font-extrabold text-base tracking-tight">
                                    Rota #{selectedCarga?.id} — {selectedCarga?.carga}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                <Badge label={selectedCarga?.filialNome} className="text-blue-700 bg-blue-100" />
                                <Badge label={selectedCarga?.tipoRota} className="text-teal-700 bg-teal-100" />
                                <Badge label={selectedCarga?.rotaGreenmileEntregueNoPrazo} className="text-green-700 bg-green-100" />
                                {selectedCarga?.rotaGreenmileImagens
                                    ? <Badge label="✔ Imagens" className="text-violet-700 bg-violet-100" />
                                    : <Badge label="✘ Sem Imagens" className="text-red-700 bg-red-100" />
                                }
                            </div>
                        </div>
                        <div className="text-right shrink-0">
                            <p className="text-[11px] mb-0.5">Operação</p>
                            <p className="font-bold text-sm">{formatLocalDate(selectedCarga?.dataOperacao)}</p>
                            <p className="text-[10px] mt-0.5">Criação: {formatLocalDateTime(selectedCarga?.dataCriacao)}</p>
                        </div>
                    </div>
                }
                footer={
                    <div className="flex justify-between pt-3">
                        <div className="flex gap-2">
                            <Button
                                label="Solicitar ajuste"
                                variant="white"
                                iconLeft="fa fa-wrench"
                                onClick={() => setCargaParaAjuste(selectedCarga)}
                            />
                            <Button
                                label="Alterar data"
                                variant="white"
                                iconLeft="fa fa-calendar"
                                onClick={abrirAlterarData}
                                className={!roleAdmin && 'd-none'}
                            />
                            <Button
                                label="Excluir"
                                variant="white"
                                iconLeft="fa fa-times"
                                onClick={excluir}
                                className={!roleAdmin && 'd-none'}
                            />
                        </div>

                        <Button
                            variant="gray"
                            label="Voltar"
                            iconLeft="fa fa-arrow-left"
                            onClick={() => { setModalResumo(false); setSelectedCarga(null) }}
                        />
                    </div>
                }
            >
                {selectedCarga &&
                    <ResumoRota rota={selectedCarga} />
                }
            </Dialog>

            <Dialog
                visible={modalAlterarData}
                onHide={() => setModalAlterarData(false)}
                header="Alterar data de operação"
                className="w-[97%] md:w-[460px]"
                blockScroll
                footer={
                    <div className="flex justify-end gap-2 pt-2">
                        <Button label="Cancelar" variant="gray" iconLeft="fa fa-arrow-left" onClick={() => setModalAlterarData(false)} />
                        <Button
                            label="Salvar nova data"
                            variant="indigo"
                            iconLeft="fa fa-save"
                            loading={salvandoData}
                            onClick={confirmarAlteracaoData}
                        />
                    </div>
                }
            >
                {selectedCarga && (
                    <div className="flex flex-col gap-3 p-1">
                        <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/30 p-3 text-xs">
                            <div>
                                <p className="text-muted-foreground">Carga</p>
                                <p className="font-semibold text-foreground">{selectedCarga.carga ?? "—"}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Transporte</p>
                                <p className="font-semibold text-foreground">{selectedCarga.transporte ?? "—"}</p>
                            </div>
                            <div className="col-span-2">
                                <p className="text-muted-foreground">Data de operação atual</p>
                                <p className="font-semibold text-foreground">{formatLocalDate(selectedCarga.dataOperacao)}</p>
                            </div>
                        </div>

                        <DateMasked
                            label="Nova data de operação"
                            required
                            value={novaDataOperacao}
                            onChange={(iso) => setNovaDataOperacao(iso)}
                        />

                        <p className="text-[11px] text-muted-foreground">
                            Somente o dia da escala é alterado: motorista, auxiliares, veículo, transporte e os vínculos
                            com Green Mile, volume e retornos continuam os mesmos. A alteração fica registrada no log.
                            A comissão não é recalculada automaticamente; gere-a novamente para as competências afetadas.
                        </p>
                    </div>
                )}
            </Dialog>

            <Dialog
                visible={!!cargaParaAjuste}
                onHide={() => setCargaParaAjuste(null)}
                header="Solicitar ajuste de comissão"
                className="w-[80%] "
                blockScroll
            >
                {cargaParaAjuste && (
                    <SolicitarAjusteRapido
                        carga={cargaParaAjuste}
                        ano={Number(ano)}
                        mes={mes}
                        onClose={() => setCargaParaAjuste(null)}
                    />
                )}
            </Dialog>
        </div>
    );
}

export default TabelaCargaEntrega;

function Badge({ label, className }) {
    return (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold tracking-wide ${className}`}>
            {label}
        </span>
    );
}